import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { PCLOUD_INTEGRATIONS } from '@/constants/integrations';
import { captureException } from '@/errors';
import { useFeatureFlagStore } from '@/stores/FeatureFlagStore';
import { initStore } from '@/stores/store-utils';
import { FeatureFlag } from '@/types/feature_flags';
import type {
  EnabledTray,
  PCloudIntegration,
  PCloudIntegrationType,
  PartnerStackIntegrationResp,
  SlackIntegration,
  TrayInstance,
  TraySolution,
  TrayStatus,
  WorkflowsWithStatus,
} from '@/types/integrations';

import { useFlashesStore } from './FlashesStore';

export const usePartnerCloudStore = defineStore('PartnerCloud', () => {
  const availableIntegrations = ref<PCloudIntegration[]>([]);
  const enabledTray = ref<EnabledTray[]>([]);
  const slackAppIntegration = ref<SlackIntegration>({ is_enabled: false });
  const partnerStackIntegration = ref<PartnerStackIntegrationResp>({
    is_enabled: false,
  });
  const inProgressInstallation = ref<string | null>(null);
  const trayStatuses = ref<TrayStatus[]>([]);

  const featureFlagStore = useFeatureFlagStore();
  const { addSuccessFlash } = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      try {
        const availableFetchedIntegrations: PCloudIntegration[] = [];
        const enabledTrayIntegrations: EnabledTray[] = [];
        const fetchedTraySolutions: Record<string, TraySolution> = {};
        const fetchedTrayInstances: Record<string, TrayInstance> = {};

        const [slackResponse, psResponse, trayResponse] = await Promise.all([
          crossbeamApi.GET('/v0.1/slack-app/slack-integration'),
          crossbeamApi.GET('/v0.1/partnerstack/integration'),
          crossbeamApi.GET('/v0.1/tray-integrations'),
        ]);

        if (slackResponse.data) slackAppIntegration.value = slackResponse.data;
        partnerStackIntegration.value =
          psResponse.data as PartnerStackIntegrationResp;

        trayResponse.data?.solutions.forEach((solution) => {
          const tag = solution.node.tags.find((tag) => tag.includes('xb'));
          if (tag) fetchedTraySolutions[tag] = { id: solution.node.id };
        });

        trayResponse.data?.instances.forEach((instance) => {
          const tag = instance.node.solution.tags.find((tag) =>
            tag.includes('xb'),
          );
          if (tag) {
            fetchedTrayInstances[tag] = {
              type: instance.node.solution.title,
              needsUpdate:
                instance.node.solutionVersionFlags
                  .requiresUserInputToUpdateVersion,
              ...instance.node,
            };
          }
        });

        if (trayResponse?.data?.statuses?.length) {
          const currentInstanceIds = new Set(
            Object.values(fetchedTrayInstances).map((instance) => instance.id),
          );
          trayStatuses.value = (
            trayResponse.data.statuses as TrayStatus[]
          ).filter((status) =>
            currentInstanceIds.has(status.solution_instance_id),
          );
        } else {
          trayStatuses.value = [];
        }

        const isTrayIntegration = (
          integration: PCloudIntegration,
        ): integration is PCloudIntegration & { trayTag: string } => {
          return integration.isTray === true && 'trayTag' in integration;
        };

        PCLOUD_INTEGRATIONS.forEach((pCloudIntegration) => {
          if (
            pCloudIntegration.type === 'partner_stack' &&
            partnerStackIntegration.value?.is_enabled
          ) {
            return;
          }

          const trayTag = isTrayIntegration(pCloudIntegration)
            ? pCloudIntegration.trayTag
            : null;

          const mergedIntegration = {
            ...(trayTag ? (fetchedTraySolutions[trayTag] ?? {}) : {}),
            ...(trayTag ? (fetchedTrayInstances[trayTag] ?? {}) : {}),
            ...pCloudIntegration,
          };

          const isTrayEnabled = !!(
            mergedIntegration.isTray && 'enabled' in mergedIntegration
          );

          /* the `unavailableWithoutFeatureFlag` property let's us hide integrations
            that aren't ready for public consumption but are ready for us to start testing!
            If it doesn't have the property specified, this returns true
            */
          const isAvailable =
            !mergedIntegration.unavailableWithoutFeatureFlag ||
            featureFlagStore.hasFeatureFlag(
              mergedIntegration.ff as FeatureFlag,
            );

          if (isTrayEnabled) {
            enabledTrayIntegrations.push(mergedIntegration as EnabledTray);
          } else if (isAvailable) {
            availableFetchedIntegrations.push(mergedIntegration);
          }
        });

        availableIntegrations.value = availableFetchedIntegrations;
        enabledTray.value = enabledTrayIntegrations;
      } catch (xhr) {
        const flashesStore = useFlashesStore();
        flashesStore.addErrorFlash({
          message: 'Failed to load some integrations',
          description: `If this error persists, please contact
              <a target="_blank" href="mailto:support@crossbeam.com">support@crossbeam.com</a>.`,
        });
      }
    },
  );

  refresh({ useCache: true });

  function getAvailableIntegrationByType(type: PCloudIntegrationType) {
    return availableIntegrations.value?.find(
      (integration) => type === integration.type,
    );
  }

  function getStatusesByInstanceId(id: string) {
    return trayStatuses.value.filter(
      (status) => status.solution_instance_id === id,
    );
  }

  function getStatusByWorkflowName(workflowName: WorkflowsWithStatus | null) {
    return trayStatuses.value.find(
      (status) => status.scheduled_workflow_name === workflowName,
    );
  }

  async function deleteIntegration(info: EnabledTray) {
    const body = { instance_id: info.id, instance_name: info.name };
    const { error } = await crossbeamApi.DELETE(`/v0.1/tray-integrations`, {
      body,
    });

    if (error) {
      captureException(error);
      throw new Error(error);
    }

    await refresh();
    addSuccessFlash({ message: 'Successfully deleted integration' });
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    refreshPartnerCloudStore: refresh,
    getAvailableIntegrationByType,
    getStatusesByInstanceId,
    getStatusByWorkflowName,
    enabledTray,
    slackAppIntegration,
    partnerStackIntegration,
    inProgressInstallation,
    availableIntegrations,
    trayStatuses,
    deleteIntegration,
  };
});
